
import React, { useEffect, useMemo } from 'react';
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  Paper,
  Breadcrumbs,
  Link as MuiLink,
  Chip
} from '@mui/material';
import {
  CheckCircle,
  ArrowRight,
  BusinessCenter,
  TrendingUp,
  Security,
  Inventory,
  Receipt,
  Settings,
  AccountBalance,
  People,
  Warehouse,
  BarChart,
  Build,
  CloudSync
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import WebsiteLayout from '../../components/Website/WebsiteLayout';

const featureIcons = [
  BusinessCenter,
  Inventory,
  TrendingUp,
  Receipt,
  Settings,
  Security,
  AccountBalance,
  People,
  Warehouse,
  BarChart,
  Build,
  CloudSync
];

const SITE_URL = 'https://www.fluxerp.co.uk';

const getCanonicalUrl = (slug) => `${SITE_URL}/${slug}`;

const getStructuredData = (config) => {
  const pageUrl = getCanonicalUrl(config.slug);

  const breadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: SITE_URL
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: config.title,
      item: pageUrl
    }
  ];

  const faqEntities = (config.faqs || []).map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }));

  const data = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: config.title,
      description: config.metaDescription,
      url: pageUrl,
      isPartOf: {
        '@type': 'WebSite',
        name: 'FluxERP',
        url: SITE_URL
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems
    }
  ];

  if (faqEntities.length > 0) {
    data.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqEntities
    });
  }

  return data;
};

// Decorative dashboard illustration for hero right side
const DashboardIllustration = () => (
  <Box sx={{
    width: '100%', borderRadius: 3, overflow: 'hidden',
    boxShadow: '0 24px 60px rgba(0,0,0,0.25)', bgcolor: '#1e3d00',
    p: 3, minHeight: 340, display: 'flex', flexDirection: 'column', gap: 2
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ff5f57' }} />
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#febc2e' }} />
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#28c840' }} />
      <Box sx={{ flex: 1, height: 6, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.08)', ml: 1 }} />
    </Box>
    <Box sx={{ display: 'flex', gap: 2 }}>
      {[{ label: 'Revenue', value: '£284K', color: '#9FE870' }, { label: 'Orders', value: '1,842', color: '#60c3f5' }, { label: 'Inventory', value: '94%', color: '#f5a623' }].map((kpi) => (
        <Box key={kpi.label} sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, p: 2, border: '1px solid rgba(255,255,255,0.08)' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', mb: 0.5 }}>{kpi.label}</Typography>
          <Typography sx={{ color: kpi.color, fontWeight: 700, fontSize: '1.1rem' }}>{kpi.value}</Typography>
        </Box>
      ))}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mt: 1, height: 100 }}>
      {[65, 80, 45, 90, 70, 55, 95, 60, 75, 85].map((h, i) => (
        <Box key={i} sx={{ flex: 1, height: `${h}%`, bgcolor: i === 9 ? '#9FE870' : 'rgba(159,232,112,0.3)', borderRadius: '4px 4px 0 0' }} />
      ))}
    </Box>
    {[1, 2, 3].map((row) => (
      <Box key={row} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: 'rgba(159,232,112,0.15)' }} />
        <Box sx={{ flex: 2, height: 8, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.08)' }} />
        <Box sx={{ flex: 1, height: 8, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ px: 1.5, py: 0.25, borderRadius: 4, bgcolor: 'rgba(159,232,112,0.15)', fontSize: '0.6rem', color: '#9FE870' }}>Active</Box>
      </Box>
    ))}
  </Box>
);

// Decorative content illustration for right side of content section
const ContentIllustration = () => (
  <Box sx={{ width: '100%', borderRadius: 3, overflow: 'hidden', boxShadow: '0 16px 48px rgba(22,51,0,0.12)', bgcolor: '#fff', border: '1px solid rgba(22,51,0,0.08)', p: 3 }}>
    <Box sx={{ bgcolor: '#163300', borderRadius: 2, p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: '#9FE870', opacity: 0.9 }} />
      <Box>
        <Box sx={{ height: 8, width: 120, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.8)', mb: 1 }} />
        <Box sx={{ height: 6, width: 80, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.4)' }} />
      </Box>
    </Box>
    <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
      {['#f0f9ea', '#e8f4fd', '#fef9ec'].map((bg, i) => (
        <Box key={i} sx={{ flex: 1, bgcolor: bg, borderRadius: 2, p: 1.5, textAlign: 'center' }}>
          <Box sx={{ height: 16, width: '60%', mx: 'auto', borderRadius: 1, bgcolor: 'rgba(22,51,0,0.15)', mb: 1 }} />
          <Box sx={{ height: 8, width: '80%', mx: 'auto', borderRadius: 1, bgcolor: 'rgba(22,51,0,0.08)' }} />
        </Box>
      ))}
    </Box>
    {[1, 2, 3, 4].map((item) => (
      <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, borderBottom: item < 4 ? '1px solid rgba(22,51,0,0.06)' : 'none' }}>
        <CheckCircle sx={{ color: '#163300', fontSize: 16, opacity: 0.7 }} />
        <Box sx={{ flex: 1, height: 8, borderRadius: 1, bgcolor: 'rgba(22,51,0,0.08)' }} />
        <Box sx={{ px: 1, py: 0.25, borderRadius: 3, bgcolor: '#f0f9ea', height: 18, width: 48 }} />
      </Box>
    ))}
  </Box>
);

const SEOLandingPage = ({ config }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [config.slug]);

  const structuredData = useMemo(
    () => getStructuredData(config),
    [config]
  );

  return (
    <WebsiteLayout>
      <Box sx={{ bgcolor: '#fff', color: '#163300' }}>
      <Helmet>
        <title>{config.title} | FluxERP</title>

        <meta
          name="description"
          content={config.metaDescription}
        />

        <link
          rel="canonical"
          href={getCanonicalUrl(config.slug)}
        />

        <meta
          property="og:title"
          content={`${config.title} | FluxERP`}
        />

        <meta
          property="og:description"
          content={config.metaDescription}
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:url"
          content={getCanonicalUrl(config.slug)}
        />

        <meta
          property="og:site_name"
          content="FluxERP"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        {structuredData.map((schema, index) => (
          <script
            key={index}
            type="application/ld+json"
          >
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>

      {/* Breadcrumbs */}
      <Container maxWidth="lg" sx={{ pt: 2, pb: 0 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <MuiLink
            component={Link}
            to="/"
            underline="hover"
            sx={{ color: '#163300', fontWeight: 500 }}
          >
            Home
          </MuiLink>
          <Typography color="text.secondary">
            {config.title}
          </Typography>
        </Breadcrumbs>
      </Container>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #163300 0%, #2a5500 60%, #1e4000 100%)',
          color: '#fff',
          py: { xs: 8, md: 12 },
          mt: 1,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute', top: -80, right: -80,
            width: 400, height: 400, borderRadius: '50%',
            bgcolor: 'rgba(159,232,112,0.06)', pointerEvents: 'none'
          }
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              {config.country && (
                <Chip label={config.country} size="small" sx={{
                  bgcolor: 'rgba(159,232,112,0.15)', color: '#9FE870',
                  fontWeight: 600, mb: 2, border: '1px solid rgba(159,232,112,0.3)'
                }} />
              )}
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.1rem', sm: '2.8rem', md: '3.5rem' },
                  fontWeight: 800,
                  lineHeight: 1.12,
                  mb: 3,
                  color: '#fff'
                }}
              >
                {config.heroTitle}
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  fontSize: { xs: '1.05rem', md: '1.2rem' }
                }}
              >
                {config.heroSubtitle}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/register"
                  sx={{
                    bgcolor: '#9FE870', color: '#163300', fontWeight: 700,
                    px: 3.5, py: 1.4, borderRadius: 2, textTransform: 'none', fontSize: '1rem',
                    '&:hover': { bgcolor: '#b6f493' }
                  }}
                >
                  Start Free Trial
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/demo"
                  sx={{
                    color: '#fff', borderColor: 'rgba(255,255,255,0.5)', fontWeight: 600,
                    px: 3, py: 1.4, borderRadius: 2, textTransform: 'none', fontSize: '1rem',
                    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' }
                  }}
                >
                  Book a Demo
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <DashboardIllustration />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits */}
      <Container
        maxWidth="lg"
        sx={{ py: { xs: 8, md: 12 } }}
      >
        <Typography
          variant="h2"
          align="center"
          sx={{
            mb: 6,
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '2.75rem' }
          }}
        >
          {config.benefitsTitle || 'Why Choose FluxERP?'}
        </Typography>

        <Grid container spacing={4}>
          {config.benefits.map((benefit, index) => {
            const Icon =
              benefit.icon || featureIcons[index % featureIcons.length];

            return (
              <Grid item xs={12} md={4} key={benefit.title}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid rgba(22,51,0,0.10)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 16px 40px rgba(22,51,0,0.10)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 52, height: 52, borderRadius: 2,
                      bgcolor: '#f0f9ea',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mb: 2.5
                    }}
                  >
                    <Icon sx={{ color: '#163300', fontSize: 28 }} />
                  </Box>

                  <Typography
                    variant="h6"
                    sx={{ mb: 1.5, fontWeight: 700, color: '#163300' }}
                  >
                    {benefit.title}
                  </Typography>

                  <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {benefit.description}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* Content Section */}
      <Box
        sx={{
          bgcolor: '#f7faf5',
          py: { xs: 8, md: 12 }
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={6}
            alignItems="center"
            direction={config.reverseContent ? 'row-reverse' : 'row'}
          >
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                sx={{
                  mb: 3,
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '2.75rem' }
                }}
              >
                {config.contentTitle}
              </Typography>

              <Typography
                sx={{
                  mb: 3,
                  fontSize: '1.1rem',
                  color: 'text.secondary',
                  lineHeight: 1.8
                }}
              >
                {config.contentBody}
              </Typography>

              <Box component="ul" sx={{ p: 0, m: 0 }}>
                {config.contentPoints?.map((point) => (
                  <Box
                    component="li"
                    key={point}
                    sx={{
                      listStyle: 'none',
                      display: 'flex',
                      alignItems: 'flex-start',
                      mb: 2
                    }}
                  >
                    <CheckCircle
                      sx={{
                        color: '#163300',
                        mr: 1.5,
                        mt: 0.3
                      }}
                      fontSize="small"
                    />

                    <Typography>{point}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              {config.contentImage ? (
                <Box
                  component="img"
                  src={config.contentImage}
                  alt={`${config.contentTitle} – FluxERP`}
                  loading="lazy"
                  sx={{ width: '100%', borderRadius: 3, boxShadow: '0 16px 48px rgba(22,51,0,0.10)' }}
                />
              ) : (
                <ContentIllustration />
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features */}
      <Container
        maxWidth="lg"
        sx={{ py: { xs: 8, md: 12 } }}
      >
        <Typography
          variant="h2"
          align="center"
          sx={{
            mb: 2,
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '2.75rem' }
          }}
        >
          {config.featuresTitle || 'Comprehensive Features'}
        </Typography>

        <Typography
          align="center"
          color="text.secondary"
          sx={{
            mb: 6,
            maxWidth: 650,
            mx: 'auto',
            lineHeight: 1.7
          }}
        >
          {config.featuresSubtitle ||
            'Explore the business management capabilities available in FluxERP.'}
        </Typography>

        <Grid container spacing={3}>
          {config.features.map((feature, index) => {
            const Icon = feature.icon || featureIcons[index % featureIcons.length];
            return (
            <Grid item xs={12} sm={6} md={3} key={feature.title}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  textAlign: 'center',
                  border: '1px solid rgba(22,51,0,0.08)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 12px 32px rgba(22,51,0,0.10)',
                    borderColor: 'rgba(22,51,0,0.18)'
                  }
                }}
              >
                <Box sx={{
                  width: 52, height: 52, borderRadius: 2, bgcolor: '#f0f9ea',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mx: 'auto', mb: 2
                }}>
                  <Icon sx={{ color: '#163300', fontSize: 26 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: '0.95rem', color: '#163300' }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* FAQs */}
      {config.faqs?.length > 0 && (
        <Box
          sx={{
            bgcolor: '#f7faf5',
            py: { xs: 8, md: 12 }
          }}
        >
          <Container maxWidth="md">
            <Typography
              variant="h2"
              align="center"
              sx={{
                mb: 6,
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' }
              }}
            >
              Frequently Asked Questions
            </Typography>

            {config.faqs.map((faq) => (
              <Box key={faq.question} sx={{ mb: 4 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 1.5,
                    fontSize: { xs: '1.15rem', md: '1.35rem' }
                  }}
                  className="faq-question"
                >
                  {faq.question}
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{ lineHeight: 1.8 }}
                  className="faq-answer"
                >
                  {faq.answer}
                </Typography>
              </Box>
            ))}
          </Container>
        </Box>
      )}

      {/* Related Pages */}
      {config.relatedPages?.length > 0 && (
        <Container
          maxWidth="lg"
          sx={{ py: { xs: 6, md: 8 } }}
        >
          <Typography
            variant="h4"
            sx={{ mb: 4, fontWeight: 700 }}
          >
            Explore More FluxERP Solutions
          </Typography>

          <Grid container spacing={2}>
            {config.relatedPages.map((page) => (
              <Grid item xs={12} sm={6} md={3} key={page.slug}>
                <Button
                  fullWidth
                  variant="outlined"
                  component={Link}
                  to={`/${page.slug}`}
                  sx={{
                    py: 1.5,
                    borderColor: '#163300',
                    color: '#163300',
                    textTransform: 'none'
                  }}
                >
                  {page.title}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* CTA */}
      <Box
        sx={{
          bgcolor: '#9FE870',
          color: '#163300',
          py: { xs: 8, md: 10 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '2.75rem' }
            }}
          >
            {config.ctaTitle ||
              'Ready to Transform Your Business?'}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              mb: 4,
              fontWeight: 400,
              opacity: 0.85,
              lineHeight: 1.6
            }}
          >
            {config.ctaSubtitle ||
              'Explore FluxERP and discover the capabilities available for your business.'}
          </Typography>

          <Button
            variant="contained"
            size="large"
            component={Link}
            to="/register"
            endIcon={<ArrowRight />}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 700,
              bgcolor: '#163300',
              color: '#fff',
              '&:hover': {
                bgcolor: '#285500'
              }
            }}
          >
            Get Started
          </Button>
        </Container>
      </Box>
      </Box>
    </WebsiteLayout>
  );
};

export default SEOLandingPage;
